import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { Donation } from '../firebase/donationService';
import { useDonationData } from '../contexts/DonationContext';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Styled components for the tracker
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1rem;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: modalFadeIn 0.3s ease-out;
  
  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f3f4f6;
    color: #1f2937;
  }
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const ProgressContainer = styled.div`
  margin-bottom: 2rem;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const ProgressLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
`;

const ProgressValue = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: var(--primary);
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 0.75rem;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
`;

const ProgressBar = styled.div<{ width: string }>`
  height: 100%;
  border-radius: 9999px;
  background-color: var(--primary);
  width: ${props => props.width};
  transition: width 1s ease-in-out;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  padding: 1.5rem;
  border-radius: 0.75rem;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &.offered {
    background-color: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.2);
  }
  
  &.target {
    background-color: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.2);
  }
`;

const StatLabel = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div<{ className?: string }>`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => {
    switch (props.className) {
      case 'offered':
        return '#2a6b34';
      case 'target':
        return '#f8b500';
      case 'donations':
      case 'pending':
      default:
        return '#4b5563';
    }
  }};
  transition: all 0.3s ease-in-out;
  
  /* Add animation for value changes */
  &.update-animation {
    animation: pulse 0.6s ease-in-out;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
      text-shadow: 0 0 10px rgba(42, 107, 52, 0.5);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const ChartContainer = styled.div`
  height: 300px;
  margin-bottom: 2rem;
  position: relative;
`;

const ChartSwitchButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #f3f4f6;
  color: #374151;
  font-weight: 600;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  display: block;
  margin: 0 auto;
  
  &:hover {
    background-color: #e5e7eb;
    transform: translateY(-1px);
  }
`;

const RecentDonationsContainer = styled.div`
  margin-top: 2rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 1.5rem;
`;

const RecentDonationsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
`;

const DonationsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DonationItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DonationDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const DonorName = styled.span`
  font-weight: 600;
  color: #374151;
`;

const DonationTime = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const DonationAmount = styled.span<{ status: 'pending' | 'completed' }>`
  font-weight: 700;
  color: ${props => props.status === 'completed' ? 'var(--primary)' : '#f59e0b'};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
`;

const Spinner = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 3px solid #e5e7eb;
  border-top-color: var(--primary);
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    to { transform: rotate(360deg) }
  }
`;

const LoadingText = styled.p`
  color: #6b7280;
  font-size: 1rem;
  text-align: center;
  margin: 0;
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 1.25rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  text-align: center;
`;

interface DonationStats {
  totalCollected: number;
  targetAmount: number;
  donationCount?: number;
  pendingDonations?: number;
}

// Donation type is imported above

interface DonationTrackerProps {
  onClose: () => void;
}

const DonationTracker: React.FC<DonationTrackerProps> = ({ onClose }) => {
  const {
    totalCollected,
    targetAmount,
    donationCount,
    pendingDonations,
    recentDonations,
    isLoading
  } = useDonationData();

  const [error, setError] = useState('');
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');
  const [isAnimating, setIsAnimating] = useState(false);
  const prevAmountRef = useRef(totalCollected);

  // Detect changes in totalCollected and animate when it changes
  useEffect(() => {
    if (prevAmountRef.current !== totalCollected && !isLoading) {
      // Only animate if this isn't the initial load
      if (prevAmountRef.current !== 0) {
        console.log(`Amount changed from ${prevAmountRef.current} to ${totalCollected}`);
        setIsAnimating(true);

        // Reset animation state after animation completes
        const timer = setTimeout(() => {
          setIsAnimating(false);
        }, 600); // Match animation duration

        return () => clearTimeout(timer);
      }

      // Update reference to current value
      prevAmountRef.current = totalCollected;
    }
  }, [totalCollected, isLoading]);

  // No need for useEffect since we're using the context

  // Calculate progress percentage
  const progressPercentage = Math.min(
    Math.round((totalCollected / targetAmount) * 100),
    100
  );

  // Define chart data for pie chart
  const pieChartData = {
    labels: ['Offered', 'Remaining'],
    datasets: [
      {
        data: [
          totalCollected,
          Math.max(targetAmount - totalCollected, 0),
        ],
        backgroundColor: ['#2a6b34', '#e2e8f0'],
        borderColor: ['#ffffff', '#ffffff'],
        borderWidth: 1,
      },
    ],
  };

  // Define chart data for bar chart
  const barChartData = {
    labels: ['Donation Progress'],
    datasets: [
      {
        label: 'Offered',
        data: [totalCollected],
        backgroundColor: '#2a6b34',
      },
      {
        label: 'Target',
        data: [targetAmount],
        backgroundColor: '#f8b500',
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  // Toggle chart type
  const toggleChartType = () => {
    setChartType(chartType === 'pie' ? 'bar' : 'pie');
  };

  if (isLoading) {
    return (
      <ModalOverlay>
        <ModalContainer>
          <LoadingContainer>
            <Spinner />
            <LoadingText>Loading donation statistics...</LoadingText>
          </LoadingContainer>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Donation Tracker</ModalTitle>
          <CloseButton onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <>
              <ProgressContainer>
                <ProgressHeader>
                  <ProgressLabel>Progress</ProgressLabel>
                  <ProgressValue>{progressPercentage}%</ProgressValue>
                </ProgressHeader>

                <ProgressBarContainer>
                  <ProgressBar width={`${progressPercentage}%`} />
                </ProgressBarContainer>
              </ProgressContainer>

              <StatsGrid>
                <StatCard className="offered">
                  <StatLabel>OFFERED</StatLabel>
                  <StatValue className={`offered ${isAnimating ? 'update-animation' : ''}`}>₹{totalCollected.toLocaleString()}</StatValue>
                </StatCard>

                <StatCard className="target">
                  <StatLabel>Target</StatLabel>
                  <StatValue className="target">₹{targetAmount.toLocaleString()}</StatValue>
                </StatCard>
              </StatsGrid>

              <ChartContainer>
                {chartType === 'pie' ? (
                  <Pie data={pieChartData} options={chartOptions} />
                ) : (
                  <Bar data={barChartData} options={chartOptions} />
                )}
              </ChartContainer>

              <ChartSwitchButton onClick={toggleChartType}>
                Switch to {chartType === 'pie' ? 'Bar' : 'Pie'} Chart
              </ChartSwitchButton>

              {/* Recent Offers Section */}
              {recentDonations.length > 0 && (
                <RecentDonationsContainer>
                  <RecentDonationsTitle>Recent Offers</RecentDonationsTitle>
                  <DonationsList>
                    {recentDonations.map((donation) => {
                      // Format the date
                      const donationDate = new Date(donation.createdAt);
                      const formattedDate = donationDate.toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      });

                      return (
                        <DonationItem key={donation.id}>
                          <DonationDetails>
                            <DonorName>{donation.name ? donation.name.substring(0, 2) : 'An'}**</DonorName>
                            <DonationTime>{formattedDate}</DonationTime>
                          </DonationDetails>
                          <DonationAmount status={donation.status}>
                            ₹{donation.amount.toLocaleString()}
                          </DonationAmount>
                        </DonationItem>
                      );
                    })}
                  </DonationsList>
                </RecentDonationsContainer>
              )}
            </>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default DonationTracker;
